import { useTranslation } from "react-i18next";
import LeafTag from "../../../../../common/leaf/LeafTag/LeafTag";
import defaultContent from "../../../../../content/leadershipSeminar";
import { getStatusTagVariant, getStatusContentKey } from "../../../../../common/util/statusConfig";
import type { PathCardProps } from "../types";
import "./PathCard.scss";
import PathRequirement from "../PathRequirement/PathRequirement";

export const PathCard = ({
  viewId,
  pathNumber,
  status,
  description,
  requirements,
  isActive,
  onClick,
}: PathCardProps) => {
  const { t } = useTranslation(["leadershipSeminar"]);

  const getStatusLabel = (): string => {
    const contentKey = getStatusContentKey(status);
    return String(t(contentKey, defaultContent[contentKey]));
  };

  const pathTitle = t("pathView.pathTitle", `Path ${pathNumber}`, { pathNumber });
  const statusLabel = getStatusLabel();
  const tagVariant = getStatusTagVariant(status);

  const getCardClassName = (): string => {
    const baseClass = "path-card";
    const classes = [baseClass];

    if (isActive) {
      classes.push(`${baseClass}--active`);
    }

    if (status === "QUALIFIED") {
      classes.push(`${baseClass}--qualified`);
    }

    return classes.join(" ");
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <div
      className={getCardClassName()}
      role="tab"
      tabIndex={0}
      aria-selected={isActive}
      aria-controls={`path-panel-${viewId}`}
      id={`path-tab-${viewId}`}
      onClick={onClick}
      onKeyDown={handleKeyDown}
    >
      <div className="path-card__header">
        <span className="path-card__title">{pathTitle}</span>
        <LeafTag variant={tagVariant} id={`path-status-${viewId}`}>
          {statusLabel}
        </LeafTag>
      </div>

      {description && <p className="path-card__description">{description}</p>}

      <div className="path-card__divider" aria-hidden="true" />

      <div
        className="path-card__requirements"
        role="list"
        aria-label={String(t("pathView.requirementsLabel", defaultContent.pathView.requirementsLabel))}
      >
        {requirements.map((req, index) => (
          <PathRequirement
            key={`${viewId}-req-${index}`}
            contentKey={req.ContentKey}
            contentParams={req.ContentParams}
            status={req.Status}
          />
        ))}
      </div>
    </div>
  );
};

export default PathCard;
